/**
 * Phase C Wave 2A.2 — notification client + constants.
 *
 * Mirrors the server-side Pydantic model + enum names verbatim so the
 * two sides can talk in strings without a translation layer.
 */
import { api } from "@/lib/api";

export const CATEGORIES = {
  general:        { label: "General",        tint: "bg-chrome-mist text-carbon" },
  operations:     { label: "Operations",     tint: "bg-coach-blue/10 text-coach-blue" },
  leasing:        { label: "Leasing",        tint: "bg-coach-blue/10 text-coach-blue" },
  drivers:        { label: "Drivers",        tint: "bg-coach-blue/10 text-coach-blue" },
  fleet:          { label: "Fleet",          tint: "bg-coach-blue/10 text-coach-blue" },
  commercial:     { label: "Commercial",     tint: "bg-touring-lime/20 text-carbon" },
  accounting:     { label: "Accounting",     tint: "bg-touring-lime/20 text-carbon" },
  safety:         { label: "Safety",         tint: "bg-critical-red/10 text-critical-red" },
  administration: { label: "Administration", tint: "bg-chrome-mist text-carbon" },
  service:        { label: "Service",        tint: "bg-amber-hold/20 text-carbon" },
  future_shop:    { label: "Future Shop",    tint: "bg-amber-hold/20 text-carbon" },
};

export const PRIORITIES = {
  informational:   { label: "Info",     dot: "bg-steel" },
  normal:          { label: "Normal",   dot: "bg-coach-blue" },
  important:       { label: "Important",dot: "bg-amber-hold" },
  action_required: { label: "Action",   dot: "bg-critical-red" },
};

// Event registry mirror — kept in sync with backend services/notifications.py
export const EVENT_TYPES = {
  TOUR_CREATED: "tour.created",
  TOUR_UPDATED: "tour.updated",
  TOUR_DRIVER_ASSIGNED: "tour.driver_assigned",
  TOUR_COACH_CHANGED: "tour.coach_changed",
  HOLD_PLACED: "hold.placed",
  HOLD_PROMOTED: "hold.promoted",
  HOLD_RELEASED: "hold.released",
  QUOTE_SENT: "quote.sent",
  QUOTE_ACCEPTED: "quote.accepted",
  QUOTE_DECLINED: "quote.declined",
  CONTRACT_EXECUTED: "contract.executed",
  COMMERCIAL_APPROVAL_REQUESTED: "commercial.approval_requested",
  COMMERCIAL_APPROVAL_APPROVED: "commercial.approval_approved",
  COMMERCIAL_APPROVAL_DECLINED: "commercial.approval_declined",
  ROUTING_DRAFT_READY: "routing.draft_ready",
  DEPARTURE_READINESS_ATTENTION: "departure.readiness_attention",
  PICKUP_SHEET_AVAILABLE: "pickup_sheet.available",
  DRIVER_HOS_ATTENTION: "driver.hos_attention",
  MOTIVE_SAFETY_EVENT: "motive.safety_event",
  FLEET_STATUS_CHANGED: "fleet.status_changed",
  SERVICE_STATUS_CHANGED: "service.status_changed",
  SHOP_WORK_ORDER_STARTED: "shop.work_order_started",
  SHOP_PARTS_NEEDED: "shop.parts_needed",
};

export const notificationsApi = {
  list: (filter = "all") =>
    api.get("/notifications", { params: { filter } }).then((r) => r.data),
  markRead: (id) =>
    api.patch(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () =>
    api.post("/notifications/mark-all-read").then((r) => r.data),
  // super_admin only test fixture; used by testing agent.
  _testInject: (payload) =>
    api.post("/notifications/_test/inject", payload).then((r) => r.data),
};

// ---- Relative time helper --------------------------------------------------
export const relativeTime = (iso) => {
  if (!iso) return "";
  const then = typeof iso === "string" ? new Date(iso) : iso;
  const diffMs = Date.now() - then.getTime();
  const s = Math.max(1, Math.floor(diffMs / 1000));
  if (s < 60) return "Just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "Yesterday";
  if (d < 7) return `${d} days ago`;
  return then.toLocaleDateString();
};
